/* eslint-disable react-hooks/exhaustive-deps */

import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { Input, FormControl, FormErrorMessage, Box, InputGroup, InputRightElement, Text } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import useHireFormErrorHandling, { ValidationType, ValidationTypeEnum } from './hooks/useHireFormErrorHandling';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { useCurrentUserProfile } from '../../../app/hooks/useCurrentUserProfile';
import theme from '../../../styles/customTheme';

const TemplateName = memo(function TemplateNameInputComponent() {
  const { templateName: contextTemplateName, formMode } = useStoreState((state) => state.formBuilder);
  const { setTemplateName, checkUniqueTemplateName, removeError } = useStoreActions((actions) => actions.formBuilder);
  const { currentUserProfile } = useCurrentUserProfile();

  const { t } = useTranslation('hrFormTemplate');

  const [inputValue, setInputValue] = useState<string>(contextTemplateName);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const { validate, error: validationError, hasError, inputRef } = useHireFormErrorHandling({
    targetId: 'root',
    innerPath: 'name',
    validationType: ValidationTypeEnum.TEMPLATE_NAME as ValidationType,
    onFocus: () => {
      inputRef.current?.focus();
    },
    removeQuestions: true,
    isAdvanced: false,
  });

  useEffect(() => {
    setInputValue(contextTemplateName);
    validate(contextTemplateName);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [contextTemplateName]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      validate(newValue);
      removeError({ targetId: 'root', innerPath: 'name' });
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [validate],
  );

  const commitChange = useCallback(() => {
    const trimmedValue = inputValue.trim();
    const isValid = validate(trimmedValue);

    if (isValid && trimmedValue !== contextTemplateName) {
      setTemplateName(trimmedValue);
    } else if (!isValid) {
      if (!trimmedValue && contextTemplateName !== '') {
        setTemplateName('');
      }
    } else if (!trimmedValue && contextTemplateName !== '' && validate('')) {
      setTemplateName('');
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [inputValue, contextTemplateName, setTemplateName, validate]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    commitChange();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [commitChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        commitChange();
        event.currentTarget.blur();
      } else if (event.key === 'Escape') {
        setInputValue(contextTemplateName);
        validate(contextTemplateName);
        event.currentTarget.blur();
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [commitChange, contextTemplateName, validate],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [setIsFocused]);

  React.useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function checkUniqueCopyName() {
    // if (formMode) return;
    if (contextTemplateName && formMode === 'copy') {
      let name = contextTemplateName;
      const newSuggestedName = await checkUniqueTemplateName({
        account: currentUserProfile?.account ?? '',
        name: contextTemplateName,
      });
      name = newSuggestedName;

      setTemplateName(name);
    }
  }

  React.useEffect(() => {
    // eslint-disable-next-line
    checkUniqueCopyName();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (document.activeElement === inputRef.current) {
          inputRef.current?.blur();
        }
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [inputRef]);

  return (
    <Box ref={containerRef} width="100%" position="relative">
      <FormControl id="template-name-form-control" isInvalid={hasError}>
        <InputGroup position="relative">
          <Input
            id="template-name-input-field"
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            placeholder={t('formBuilder.templateNamePlaceholder', 'Enter template name...')}
            size="sm"
            variant={isFocused ? 'outline' : 'unstyled'}
            data-testid="template-name-input"
            isInvalid={hasError}
            aria-describedby={hasError ? 'template-name-error' : undefined}
            autoComplete="off"
            sx={{
              fontWeight: 'bold',
              borderRadius: 'md',
              fontSize: 'md',
            }}
            transition="none"
          />
          {hasError && validationError && (
            <FormErrorMessage
              id="template-name-error"
              sx={{
                position: 'absolute',
                bottom: '-20px',
                fontSize: 'xs',
                color: theme.colors.red[500],
              }}
            >
              {validationError}
            </FormErrorMessage>
          )}
          <InputRightElement width="auto" px={2}>
            {isFocused && (
              <Box position="absolute" right="9px" width="max-content">
                <Text fontSize="11px" color="gray.400">
                  {t('formBuilder.enterToSave')}
                </Text>
              </Box>
            )}
          </InputRightElement>
        </InputGroup>
      </FormControl>
    </Box>
  );
});

export default TemplateName;
